import engine from "app/core/liquid/engine"
import { Token, Tokenizer, TokenKind } from "liquidjs"
// Let's say we only allow:
// HTML, Value, Literal, Number

// Number = 1,
// Literal = 2,
// Tag = 4,
// Output = 8,
// HTML = 16,
// Filter = 32,
// Hash = 64,
// PropertyAccess = 128,
// Word = 256,
// Range = 512,
// Quoted = 1024,
// Operator = 2048,
// Delimited = 12
const allowedTokens = [TokenKind.Literal, TokenKind.Number, TokenKind.Output, TokenKind.HTML]

export const getTokens = (input: string) => {
  const tokenizer = new Tokenizer(input, engine.options.operatorsTrie)
  const tokens = tokenizer.readTopLevelTokens(engine.options).map((token) => {
    if (!allowedTokens.includes(token.kind)) {
      throw new Error("Unknown token")
    }
    return token
  })
  return tokens
}

export const templateToLocalizableString = (input: string) => {
  const tokens = getTokens(input.trim())
  var translatableStrings = tokens
    .map((t) => {
      switch (t.kind) {
        case TokenKind.Literal: {
          return `{}`
        }
        case TokenKind.Number: {
          return `{}`
        }
        case TokenKind.Output: {
          return `{}`
        }
      }
      return t.getText()
    })
    .join("")
  return translatableStrings
}

export const getAllTokens = (input: string) => {
  const tokenizer = new Tokenizer(input, engine.options.operatorsTrie)
  const tokens = tokenizer.readTopLevelTokens(engine.options).map((token) => {
    if (![...allowedTokens, TokenKind.Tag].includes(token.kind)) {
      throw new Error("Unknown token")
    }
    return token
  })
  return tokens
}

/**
 * Parses all HTML groups and concatenates
 * neighboring HTML & Output strings, while breaking on tags
 *
 * @param input Raw template input
 * @returns All strings which can be localized
 */
export const templateToLocalizableStrings = (input: string) => {
  const tokens = getAllTokens(input.trim())
  var translatableStrings = tokens
    .filter((t) => t.input.length < 751) // This is a limitation of how string localization works. Can't be longer than 750 characters.
    .map((t) => {
      switch (t.kind) {
        case TokenKind.Literal: {
          return `{}`
        }
        case TokenKind.Number: {
          return `{}`
        }
        case TokenKind.Output: {
          return `{}`
        }
        case TokenKind.Tag: {
          // break on the tag
          return null
        }
      }
      return t.getText()
    })
  // Creates groups [["Hello", "{{}}"], []]
  return (
    translatableStrings
      .reduce(
        (prev, current) => {
          // If the current is null, it indicates a break which we need to stich all previous records together
          if (!current) {
            return [...prev, []]
          }
          const last: string[] = prev.pop() as string[]
          return [...prev, [...last, current]]
        },
        [[]]
      )
      // Remove empties [[], ["Hello"]] -> [["Hello"]]
      .filter((x) => x.length !== 0)
      // Join groups [["Hello ", "{}"]] -> ["Hello {}"]
      .map((x) => x.join(""))
      .filter((x) => !x.match(/^\s*(\{\})?\s*$/))
  )
}

// 1. Extract the function to lowercase localizations
const getLowercaseLocalizations = (
  localizations: Record<string, string>
): Record<string, string> => {
  return Object.keys(localizations).reduce(
    (prev, key) => ({ ...prev, [key.toLowerCase()]: localizations[key] }),
    {}
  )
}

// 2. Extract the grouping of tokens
const groupTokens = (template: string): Token[][] => {
  return getAllTokens(template.trim())
    .reduce(
      (prev, current) => {
        if (current.kind === TokenKind.Tag) {
          return [...prev, [current], []]
        }
        const last: Token[] = prev.pop() as Token[]
        return [...prev, [...last, current]]
      },
      [[]]
    )
    .filter((x) => x.length !== 0)
}

// 3. Extract the translation and mapping logic
const translateTokens = (
  tokenGroup: Token[],
  localizations: Record<string, string>,
  lowercaseLocalizations: Record<string, string>
): string => {
  if (tokenGroup[0]?.kind === TokenKind.Tag) {
    return tokenGroup[0].getText()
  }

  const translatableTemplate = tokenGroup
    .map((t) => {
      switch (t.kind) {
        case TokenKind.Literal:
        case TokenKind.Number:
        case TokenKind.Output:
          return `{}`
        case TokenKind.Tag:
          throw new Error("Internal inconsistency error")
        default:
          return t.getText()
      }
    })
    .filter((x) => !!x)
    .join("")

  const translatedSubstitutedTemplate = translatableTemplate.match(/^\s*\{\}\s*$/)
    ? translatableTemplate
    : localizations[translatableTemplate] ||
      lowercaseLocalizations[translatableTemplate.toLowerCase()] ||
      translatableTemplate

  const faltMappedValue = flattenAndFilterMapping(translatedSubstitutedTemplate)

  const placeholderCountInFaltMappedValue = faltMappedValue.filter((item) => item === `{}`).length

  const placeholderCountInTokenGroup = tokenGroup.filter((t) => t.kind !== TokenKind.HTML).length

  if (placeholderCountInFaltMappedValue !== placeholderCountInTokenGroup) {
    throw new Error(
      "Internal inconsistency error: the number of placeholders in the translated group must match the original token group"
    )
  }

  const substitutionTokens = tokenGroup.filter((t) => t.kind !== TokenKind.HTML)
  return faltMappedValue
    .map((item, index) => {
      if (item === `{}`) {
        return substitutionTokens.shift()?.getText() || ""
      }
      return item
    })
    .join("")
}

// 4. Extract the flattening and filtering logic
const flattenAndFilterMapping = (translatedSubstitutedTemplate: string): string[] => {
  return translatedSubstitutedTemplate
    .split("{}")
    .flatMap((value, index, array) => (array.length - 1 !== index ? [value, `{}`] : value))
    .filter((value, index, arr) => {
      if (index === 0 && value === "") {
        return false
      }
      if (index === arr.length - 1 && value === "") {
        return false
      }
      return true
    })
}

// The main function
export const localizeComplexTemplate = (
  template: string,
  localizations: Record<string, string>
): string => {
  const lowercaseLocalizations = getLowercaseLocalizations(localizations)

  return groupTokens(template)
    .map((tokenGroup) => translateTokens(tokenGroup, localizations, lowercaseLocalizations))
    .join("")
}

export class Localizer {
  private lowercasedLocalizations: Record<string, string> = {}
  constructor(private localizations: Record<string, string>) {
    this.lowercasedLocalizations = getLowercaseLocalizations(localizations)
  }

  public localizeComplexTemplate = (template: string): string => {
    return groupTokens(template)
      .map((tokenGroup) =>
        translateTokens(tokenGroup, this.localizations, this.lowercasedLocalizations)
      )
      .join("")
  }

  public safelyLocalizeTemplate = (template: string): string => {
    try {
      return this.localizeComplexTemplate(template)
    } catch (e) {
      console.error(`Failed to localize template`, e)
      return template
    }
  }
}

export const localizeTemplate = (
  template: string,
  localizations: Record<string, string>
): string => {
  const expressions = getTokens(template.trim()).filter((t) => t.kind !== TokenKind.HTML)
  const translatableTemplate = templateToLocalizableString(template)
  const translatedSubstitutedTemplate = localizations[translatableTemplate]
  if (!translatedSubstitutedTemplate) {
    throw new Error(`No translation found`)
  }
  const translatedTemplate = translatedSubstitutedTemplate
    .split("{}")
    .flatMap((value, index, array) =>
      array.length - 1 !== index // check for the last item
        ? [value, expressions.pop()]
        : value
    )
    .map((v) => (typeof v === "string" ? v : v?.getText()))
    .join("")
  return translatedTemplate
}

export const render = async (
  template: string,
  input: Record<string, any>,
  localizations?: Record<string, any>
) => {
  const localizer = new Localizer(localizations || {})
  const localizedTemplate = localizer.safelyLocalizeTemplate(template)
  return engine.parseAndRender(localizedTemplate, input)
}
